import React, { FC } from 'react';
import {
    Checkbox as DefaultCheckbox,
    checkboxClasses,
    CheckboxProps as DefaultCheckboxProps,
} from '@mui/material';

import { Box } from '../Box';


const iconPxForSize: Record<CheckboxProps['size'], number> = {
    medium: 6,
    small: 5,
};

const BpIcon: FC<Pick<CheckboxProps, 'readOnly' | 'size'>> = ({ readOnly, size }) => (
    <Box
        component='span'
        sx={({ spacing }) => ({
            borderRadius: spacing(1),
            width: spacing(iconPxForSize[size] - 1),
            height: spacing(iconPxForSize[size] - 1),
            minWidth: spacing(iconPxForSize[size] - 1),
            border: 2,
            borderColor: 'grayscale.shade60',
            'input:hover ~ &': {
                borderColor: !readOnly && 'grayscale.shade80',
            },
            'input:disabled ~ &': !readOnly ? {
                backgroundColor: 'grayscale.shade20',
                borderColor: 'grayscale.shade20',
            } : {},
        })}
    />
);

const CheckIcon = (
    <svg
        viewBox='0 0 24 24'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
    >
        <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M20.7071 5.29289C21.0976 5.68342 21.0976 6.31658 20.7071 6.70711L9.70711 17.7071C9.31658 18.0976 8.68342 18.0976 8.29289 17.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929C3.68342 10.9024 4.31658 10.9024 4.70711 11.2929L9 15.5858L19.2929 5.29289C19.6834 4.90237 20.3166 4.90237 20.7071 5.29289Z'
        />
    </svg>
);

export type CheckboxProps = Pick<DefaultCheckboxProps, 'checked' | 'onChange' | 'disabled' | 'required' | 'size'> & {
    color?: 'primary' | 'secondary';
    name?: string;
    readOnly?: boolean;
};

export const Checkbox: FC<CheckboxProps> = ({
    checked,
    onChange,
    disabled,
    color = 'primary',
    name,
    required,
    readOnly,
    size = 'medium',
    ...props
}) => (
    <DefaultCheckbox
        sx={({ spacing, palette }) => ({
            width: spacing(iconPxForSize[size]),
            height: spacing(iconPxForSize[size]),
            minWidth: spacing(iconPxForSize[size]),
            padding: 0,
            borderRadius: spacing(1.5),

            '& > svg': {
                width: spacing(iconPxForSize[size] - 1),
                height: spacing(iconPxForSize[size] - 1),

                fill: palette.grayscale.shade0,
            },

            [`&.${checkboxClasses.checked}`]: {
                [`&.${checkboxClasses.colorPrimary}`]: {
                    backgroundColor: 'general.primary50',
                    '&:hover': {
                        backgroundColor: !readOnly && 'general.primary60',
                    },
                },
                [`&.${checkboxClasses.colorSecondary}`]: {
                    backgroundColor: 'general.secondary50',
                    '&:hover': {
                        backgroundColor: !readOnly && 'general.secondary60',
                    },
                },
                [`&.${checkboxClasses.disabled}`]: !readOnly ? {
                    backgroundColor: 'grayscale.shade20',

                    '& > svg': {
                        fill: palette.grayscale.shade80,
                    },
                } : {},
            },
        })}
        data-testid={`checkbox-${name}`}
        name={name}
        icon={<BpIcon readOnly={readOnly} size={size} />}
        checkedIcon={CheckIcon}
        checked={checked}
        onChange={onChange}
        disabled={disabled || readOnly}
        disableRipple
        color={color}
        required={required}
        {...props}
    />
);
